import React, { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import * as transactionService from "@/shared/api/services/transactionService";
import * as engineService from "@/shared/api/services/engineService";
import { mapGlobalTransactions } from "@/shared/utils/transactionMappers";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/shared/components/ui/sheet";
import {
  Search,
  ArrowDownToLine,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  Download,
} from "lucide-react";
import EngineLayout from "@/shared/components/engine/EngineLayout";

const statusConfigDefault = {
  success: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: "Success" },
  pending: { icon: Clock, color: "bg-amber-100 text-amber-700", label: "Pending" },
  processing: { icon: Loader2, color: "bg-blue-100 text-blue-700", label: "Processing" },
  failed: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Failed" },
  declined: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Declined" },
  cancelled: { icon: XCircle, color: "bg-slate-100 text-slate-700", label: "Cancelled" },
  expired: { icon: XCircle, color: "bg-slate-100 text-slate-700", label: "Expired" },
};

const getStatusConfig = (t) => {
  if (!t) return statusConfigDefault;
  return {
    success: { ...statusConfigDefault.success, label: t("success") },
    pending: { ...statusConfigDefault.pending, label: t("pending") },
    processing: { ...statusConfigDefault.processing, label: t("processing") },
    failed: { ...statusConfigDefault.failed, label: t("failed") },
    declined: { ...statusConfigDefault.declined, label: t("declined") },
    cancelled: { ...statusConfigDefault.cancelled, label: t("cancelled") || "Cancelled" },
    expired: { ...statusConfigDefault.expired, label: t("expired") },
  };
};

export default function EngineDeposits() {
  const { t } = useLanguage();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedTx, setSelectedTx] = useState(null);
  const resellerName = "Gaming Partners Ltd";
  const statusConfig = useMemo(() => getStatusConfig(t), [t]);

  const [merchantIds, setMerchantIds] = useState([]);

  // Fetch All Merchants (Engine's + Resellers')
  const { data: allMerchants = [], isLoading: merchantsLoading } = useQuery({
    queryKey: ["engine-all-merchants"],
    queryFn: async () => {
      let allMerchantIdes = [];
      let allMerchantsList = [];
      
      // 1. Get all merchants created by engine logged in user
      const ownedMerchants = await engineService.getMyMerchants();
      ownedMerchants.forEach(m => {
        allMerchantIdes.push(m.id);
        allMerchantsList.push(m);
      });
      
      // 2. Get all the resellers created by engine logged in user
      const resellers = await engineService.getMyResellers();
      const resellerIdes = resellers.map(r => r.id);
      
      // 3. Then one by one get all the merchant ides created by resellers using resellerIdes
      for (const rId of resellerIdes) {
        const resellerMerchants = await engineService.getResellerMerchants(rId);
        resellerMerchants.forEach(m => {
          if (!allMerchantIdes.includes(m.id)) {
            allMerchantIdes.push(m.id);
            allMerchantsList.push(m);
          }
        });
      }
      
      return allMerchantsList;
    },
    refetchInterval: 60000,
  });

  useEffect(() => {
    if (allMerchants.length > 0) {
      setMerchantIds(allMerchants.map(m => m.id));
    } else if (!merchantsLoading) {
      setMerchantIds(["NO_MERCHANT_FOUND"]);
    }
  }, [allMerchants, merchantsLoading]);

  const { data: paginationResult, isLoading: transactionsLoading } = useQuery({
    queryKey: ["engine-deposits-paginated", search, statusFilter, merchantIds],
    queryFn: () => {
      const filters = { type: "deposit" };
      if (statusFilter !== "all") filters.status = statusFilter;

      return transactionService.getPaginatedTransactions(
        1,
        100,
        search,
        filters,
        merchantIds
      );
    },
    enabled: merchantIds.length > 0,
    keepPreviousData: true,
  });

  const transactions = useMemo(() => {
    return mapGlobalTransactions(paginationResult?.data || []);
  }, [paginationResult]);

  const isLoading = transactionsLoading || (merchantsLoading && !merchantIds.length);

  const totalAmount = transactions
    .filter(t => ["success", "paid", "completed"].includes(String(t.status || "").toLowerCase()))
    .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);

  const TransactionRowSkeleton = () => (
    <tr className="animate-pulse">
      <td className="px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-slate-100" />
          <div className="space-y-2">
            <div className="h-4 w-24 bg-slate-100 rounded" />
            <div className="h-3 w-16 bg-slate-50 rounded" />
          </div>
        </div>
      </td>
      <td className="px-4 py-4"><div className="h-4 w-24 bg-slate-50 rounded" /></td>
      <td className="px-4 py-4 text-right"><div className="h-4 w-20 bg-slate-50 rounded ml-auto" /></td>
      <td className="px-4 py-4"><div className="h-7 w-20 bg-slate-50 rounded-full" /></td>
      <td className="px-4 py-4"><div className="h-4 w-24 bg-slate-50 rounded" /></td>
    </tr>
  );

  return (
    <EngineLayout currentPage="EngineDeposits" resellerName={resellerName}>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t("deposits")}</h1>
            <p className="text-slate-500">{t("total")}: RS{totalAmount.toLocaleString()}</p>
          </div>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            {t("export")}
          </Button>
        </div>

        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder={`${t("search")}...`} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder={t("status")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allStatus")}</SelectItem>
                <SelectItem value="success">{t("success")}</SelectItem>
                <SelectItem value="pending">{t("pending")}</SelectItem>
                <SelectItem value="processing">{t("processing")}</SelectItem>
                <SelectItem value="declined">{t("declined")}</SelectItem>
                <SelectItem value="failed">{t("failed")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("transaction")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden sm:table-cell">{t("merchant")}</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("amount")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("status")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden md:table-cell">{t("date")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoading && !transactions.length ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <TransactionRowSkeleton key={i} />
                  ))
                ) : transactions.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-12 text-slate-500">{t("noDeposits")}</td></tr>
                ) : (
                  transactions.map((tx, i) => {
                    const statusKey = (tx.status || '').toLowerCase();
                    const status = statusConfig[statusKey] || statusConfig.pending;
                    const StatusIcon = status.icon;
                    return (
                      <motion.tr
                        key={tx.id || i}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: i * 0.02 }}
                        onClick={() => setSelectedTx(tx)}
                        className="hover:bg-slate-50 cursor-pointer"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-sky-100 flex items-center justify-center">
                              <ArrowDownToLine className="h-4 w-4 text-sky-600" />
                            </div>
                            <div>
                              <p className="font-mono text-sm">{(tx.id || '').slice(0, 10)}...</p>
                              <p className="text-xs text-slate-400 sm:hidden">{tx.merchant?.name || tx.merchant?.code || t("nA")}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell text-sm">{tx.merchant?.name || tx.merchant?.code || t("nA")}</td>
                        <td className="px-4 py-3 text-right font-semibold text-sky-600">
                          {(() => {
                            const symbol = tx.currency === "PKR" ? "Rs" : "฿";
                            return `+${symbol}${(Number(tx.amount || 0)).toLocaleString()}`;
                          })()}
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`${status.color} gap-1 border-0`}>
                            <StatusIcon className={`h-3 w-3 ${statusKey === "processing" ? "animate-spin" : ""}`} />
                            {status.label}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500 hidden md:table-cell">
                          {tx.createdAt ? format(new Date(tx.createdAt), "MMM d, HH:mm") : t("nA")}
                        </td>
                      </motion.tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <Sheet open={!!selectedTx} onOpenChange={() => setSelectedTx(null)}>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
            {selectedTx && (
              <>
                <SheetHeader className="pb-4 border-b">
                  <SheetTitle className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
                      <ArrowDownToLine className="h-5 w-5 text-sky-600" />
                    </div>
                    <div>
                      <p className="text-lg">{t("deposit")}</p>
                      <Badge className={`${statusConfig[(selectedTx.status || '').toLowerCase()]?.color || statusConfig.pending.color} border-0`}>
                        {statusConfig[(selectedTx.status || '').toLowerCase()]?.label || t("pending")}
                      </Badge>
                    </div>
                  </SheetTitle>
                </SheetHeader>

                <div className="py-6 space-y-6">
                  <div className="text-center py-4 bg-slate-50 rounded-xl">
                    <p className="text-sm text-slate-500 mb-1">{t("amount")}</p>
                    <p className="text-3xl font-bold text-sky-600">
                      {(() => {
                        const symbol = selectedTx.currency === "PKR" ? "Rs" : "฿";
                        return `${symbol}${Number(selectedTx.amount || 0).toLocaleString()}`;
                      })()}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-500">{t("merchant")}</p>
                        <p className="font-medium">{selectedTx.merchant?.name || selectedTx.merchant?.code || t("nA")}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">{t("type")}</p>
                        <p className="font-medium capitalize">{selectedTx.type}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-500">{t("provider")}</p>
                        <p className="font-medium">{selectedTx.provider?.code || t("nA")}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">{t("reference")}</p>
                        <p className="font-medium">{selectedTx.merchantRef || t("nA")}</p>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-slate-500 mb-2">{t("transactionId")}</p>
                      <code className="block bg-slate-100 px-3 py-2 rounded-lg font-mono text-sm">
                        {selectedTx.id}
                      </code>
                    </div>

                    <div>
                      <p className="text-sm text-slate-500 mb-2">{t("date")}</p>
                      <p className="font-medium">
                        {selectedTx.createdAt ? format(new Date(selectedTx.createdAt), "PPP p") : t("nA")}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </EngineLayout>
  );
}